import { fmtMoney } from "@/lib/format";
import { menuImagePreferredSrc } from "@/lib/menuImage";

// Shared by the No AI kiosk / online / QR ordering page AND the AI Cashier's Browse Menu — one menu design.
export function MenuBrowser({ cats, activeCat, setActiveCat, items, isKiosk, onAdd }) {
  return (
    <>
      {/* Category tabs — rows of 5 (4 on kiosk lg, 5 on kiosk xl) instead
          of a horizontal scroller. Matches POS layout for staff/customer
          consistency. */}
      <div
        data-testid="co-categories"
        className={`grid gap-2 ${isKiosk ? "grid-cols-3 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 mb-6" : "grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 mb-4"}`}
      >
        {cats.map((c) => (
          <button
            key={c.id}
            data-testid={`co-cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setActiveCat(c.id)}
            className={`pill-tab whitespace-nowrap truncate ${activeCat === c.id ? "pill-tab-active" : "pill-tab-inactive"} ${isKiosk ? "h-12 px-4 text-base" : ""}`}
          >{c.name}</button>
        ))}
      </div>

      <div className={`grid ${isKiosk ? "grid-cols-2 lg:grid-cols-3 gap-5" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"}`}>
        {items.map((it) => <MenuItemCard key={it.id} it={it} isKiosk={isKiosk} onAdd={onAdd} />)}
        {items.length === 0 && <div className="col-span-full text-center py-12 text-[var(--ink-500)] text-sm">No items in this category yet.</div>}
      </div>
    </>
  );
}

// Unavailable / sold-out items stay in the grid as dimmed, non-clickable cards so the category stays intact.
function MenuItemCard({ it, isKiosk, onAdd }) {
  const isUnavailable = it.available === false;
  const isSoldOut = !!it.sold_out;
  const disabled = isUnavailable || isSoldOut;
  const src = menuImagePreferredSrc(it, { prefer: isKiosk ? "display" : "thumb" });
  return (
    <button
      data-testid={`co-item-${it.name.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={() => onAdd(it)}
      disabled={disabled}
      aria-disabled={disabled}
      className={`neumo rounded-xl overflow-hidden text-left active:scale-[0.97] relative ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      {src ? (
        <img src={src} alt={it.name} loading="lazy" className={`w-full ${isKiosk ? "h-44" : "h-28"} object-cover`} />
      ) : (
        <div className={`w-full ${isKiosk ? "h-44" : "h-28"} bg-[var(--sand-100)] flex items-center justify-center text-[var(--ink-500)] text-2xl font-heading`}>
          {it.name?.slice(0, 1) || "?"}
        </div>
      )}
      <div className={isKiosk ? "p-4" : "p-3"}>
        <div className={`font-medium ${isKiosk ? "text-lg" : "text-sm"} leading-tight`}>{it.name}</div>
        {it.description && <div className={`text-[var(--ink-500)] mt-1 ${isKiosk ? "text-sm" : "text-xs"} line-clamp-2`}>{it.description}</div>}
        <div className={`tabular font-medium text-[var(--terracotta)] mt-2 ${isKiosk ? "text-lg" : "text-sm"}`}>{fmtMoney(it.price)}</div>
        {/* "Only N left" nudge — hides once stock reaches zero (the disabled overlay takes over). */}
        {it.stock && !disabled && Number(it.stock.remaining_display) > 0 && Number(it.stock.remaining_display) <= 5 && (
          <div
            data-testid={`co-item-${it.id}-stock-nudge`}
            data-stock-remaining={it.stock.remaining_display}
            className="text-[11px] text-amber-700 mt-1"
          >
            Only {it.stock.remaining_display} left
          </div>
        )}
      </div>
      {disabled && (
        <div
          data-testid={isUnavailable ? `co-item-${it.id}-unavailable` : `co-item-${it.id}-soldout`}
          className="absolute inset-0 bg-white/70 rounded-xl flex items-center justify-center text-xs font-medium pointer-events-none"
        >
          {isUnavailable ? "Currently unavailable" : "Sold out"}
        </div>
      )}
    </button>
  );
}
